import type { PlacementUserInstructionAction } from '~/modules/query/ai/types';

const PROMPT_ASSIST_INTERNAL_ERROR_PATTERN = /\b(api|endpoint|transform|request)\b/i;

type NonCustomPromptAssistAction = Exclude<PlacementUserInstructionAction, 'custom-prompt'>;

const DEFAULT_ACTION_INSTRUCTIONS: Record<NonCustomPromptAssistAction, string> = {
  rewrite: 'Rewrite the original input for clarity while preserving meaning and key details.',
  shorten: 'Shorten the original input while preserving its meaning and required details.',
  expand:
    'Expand the original input with useful detail while keeping the original intent. Not more than 3 lines.',
  proofread:
    'Proofread the original input for grammar, spelling, and punctuation without changing intent.',
  'more-professional':
    'Rewrite the original input in a more professional tone while preserving intent.',
  'more-funny':
    'Rewrite the original input with a more playful and funny tone while preserving intent.',
};

export interface PromptAssistError {
  message: string;
  status?: number;
}

export function buildAiAssistInstruction(
  action: PlacementUserInstructionAction,
  instruction?: string,
): string {
  const extraInstruction = instruction?.trim();

  if (action === 'custom-prompt') {
    if (!extraInstruction) {
      return 'Rewrite the original input according to the modification instructions while preserving intent.';
    }

    return extraInstruction;
  }

  const baseInstruction = DEFAULT_ACTION_INSTRUCTIONS[action];

  if (!extraInstruction) {
    return baseInstruction;
  }

  return `${baseInstruction}\n\nAdditional instruction: ${extraInstruction}`;
}

export function formatPromptAssistErrorMessage(error: PromptAssistError): string {
  const message = error.message.trim();
  if (!message) {
    return 'Could not generate a draft right now. Please try again.';
  }

  if (
    PROMPT_ASSIST_INTERNAL_ERROR_PATTERN.test(message) ||
    (typeof error.status === 'number' && error.status >= 500)
  ) {
    return 'Could not generate a draft right now. Please try again.';
  }

  return message;
}

/** The HTTP status carried by an RTK Query / axios rejection, when the thrown value has one. */
export function promptAssistErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;

  return typeof status === 'number' ? status : undefined;
}
